/**
 * @module ui/dialog-manager
 */
import { registerLayer, unregisterLayer } from "panther/scroll-manager";
import ModalDialog from "panther/modal-dialog";
import ee from "lib/eventemitter";
import { toArray } from "utils/utils";

const rootElement = document.getElementById("pb-root");

export let dialogs = [];

/**
 * @method getDialogById
 * @description Get a desired dialog by it's unique id
 * @param {String} dialogId - the ID of a modal dialog element
 * @returns {Object} of dialog
 */
export function getDialogById(dialogId) {
  return dialogs.find(dialog => dialog.getRootElement().id === dialogId);
}

/**
 * @method registerDialog
 * @description initialize and register a modal dialog
 * @param {String} dialogId - the ID of a modal dialog element
 * @param {Object} [callbacks]
 * @param {Function} [callbacks.init] - executed after the dialog has been initialized and registered
 * @param {Function} [callbacks.populate] - executed as the dialog is opening
 * @param {Function} [callbacks.depopulate] - executed after the dialog has closed
 * @param {Object} options
 * @returns {Object}
 */
export function registerDialog(dialogId, callbacks = null, options = {}) {
  const dialogElement = document.getElementById(dialogId);
  const openButtons = document.querySelectorAll(`[data-popup="${dialogId}"]`);
  if (!dialogElement || !openButtons.length) {
    return;
  }
  // For progressive enhancement, don't add the ARIA attribute until the dialog is created
  toArray(openButtons).forEach(button => button.setAttribute("aria-haspopup", true));

  // Create modal dialog "instance" (which is actually just an object that links
  // to the ModalDialog object via its prototype chain - the OLOO pattern)
  const dialog = Object.create(ModalDialog);
  dialog.init(openButtons, dialogElement, options);

  registerLayer({
    key: "dialog-manager",
    isOpen: dialog.isOpen.bind(dialog),
    lockEvent: "tgam-modal-dialog:open",
    unlockEvent: "tgam-modal-dialog:closing",
    saveScrollEvent: "tgam-modal-dialog:open",
    resetScrollEvent: "tgam-modal-dialog:closed"
  });
  dialogs.push(dialog);

  if (callbacks) {
    dialog.customCallbacks = callbacks;
    if (callbacks.init) {
      callbacks.init(dialog);
    }
  }
  return dialog;
}

/**
 * @method unregisterDialog
 * @description unregisters dialog with scroll manager
 * @param {String} dialogId
 */
export function unregisterDialog(dialogId) {
  dialogs = dialogs.filter(dialog => {
    if (dialog.getRootElement().id === dialogId) {
      unregisterLayer({
        key: "dialog-manager"
      });
      return false;
    }
    return true;
  });
}

/**
 * @method populateDialog
 * @description Callback when dialog is opening.
 * @param {Object} dialog
 */
function populateDialog(dialog) {
  if (dialog.customCallbacks && dialog.customCallbacks.populate) {
    dialog.customCallbacks.populate(dialog);
  }
}

/**
 * @method depopulateDialog
 * @description Callback when dialog is closed.
 * @param {Object} dialog
 */
function depopulateDialog(dialog) {
  if (dialog.customCallbacks && dialog.customCallbacks.depopulate) {
    if (dialog.customCallbacks.depopulate(dialog)) {
      dialog.setLabel("");
      dialog.clear();
    }
  } else {
    dialog.setLabel("");
    dialog.clear();
  }
}

/**
 * @method dialogOpening
 * @param {Object} dialog
 */
function dialogOpening(dialog) {
  rootElement.setAttribute("aria-hidden", true);
  populateDialog(dialog);
}

/**
 * @method dialogClosing
 * @description Remove aria-hidden from the root element if all dialogs are closed at this point in time
 */
function dialogClosing() {
  if (allDialogsClosed()) {
    rootElement.removeAttribute("aria-hidden");
  }
}

/**
 * @method dialogClosed
 * @description Cleanup after the dialog has been closed.
 * @param {Object} dialog
 */
function dialogClosed(dialog) {
  if (allDialogsClosed()) {
    rootElement.removeAttribute("aria-hidden");
  }
  depopulateDialog(dialog);
}

/**
 * @method allDialogsClosed
 * @description Loops through all dialog instances and checks to see if any of them are still open
 * @returns {Boolean} Returns true if all dialogs are closed
 */
function allDialogsClosed() {
  return dialogs.every(dialog => dialog.isOpen && !dialog.isOpen());
}

/**
 * @method init
 * @description for accessibility reasons, we need to apply aria-hidden to all elements that are obscured by the modal dialog when it's open
 */
function init() {
  ee.addListener("tgam-modal-dialog:opening", dialogOpening);
  ee.addListener("tgam-modal-dialog:closing", dialogClosing);
  ee.addListener("tgam-modal-dialog:closed", dialogClosed);
}

init();
